import { CardMedia, Typography } from "@material-ui/core";
import { isRejected } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { andThen, pipe } from "ramda";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import icon from "../../../assets/icons/error.svg";
import { Heading } from "../../../components/Heading";
import { selectImages } from "../../../components/ImageUploader/images.slice";
import { Images } from "../../../components/ImageUploader/types";
import { convertBlobsToFiles } from "../../../components/ImageUploader/utilities/convertBlobsToFiles";
import {
  selectFormType,
  selectPrevRoute,
} from "../../../components/formSettings.slice";
import { useHideFooter, usePreventRefresh } from "../../../hooks";
import { Url } from "../../../router/enums";
import { mapProgressRouteToSuccess } from "../../../router/utilities";
import { RootState } from "../../../store";
import {
  selectACFormData,
  selectOCVehicleFormData,
} from "../../../store/rootSelectors";
import { GenericScreen } from "../../GenericScreen";
import {
  doesErrorOccured,
  generatePDF,
  isCaseSentSuccessfully,
  postTextData,
  postImages,
  fulfillSender,
} from "../sender.slice";

import styles from "./Progress.module.scss";

const prepareImageToUpload = (images: Record<string, Images>) => {
  const data: { [key: string]: Array<string> } = {};
  for (const [key, values] of Object.entries(images)) {
    data[key] = Object.values(values).map((image) => image.src);
  }

  return data;
};

const convertBlobsToImages = async (images: {
  [key: string]: Array<string>;
}) => {
  const data: { [key: string]: Array<string> } = {};
  for (const [key, values] of Object.entries(images)) {
    data[key] = await convertBlobsToFiles(values);
  }

  return data;
};

const convertToDispatchActions = (images: { [key: string]: Array<string> }) => {
  const actions = Object.keys(images).map((imageKey) => () =>
    postImages({ type: imageKey, images: images[imageKey] })
  );

  return actions;
};

const prepareUploadImagesAction = pipe(
  prepareImageToUpload,
  convertBlobsToImages,
  andThen(convertToDispatchActions)
);

export type SendingProgressProps = {
  readonly formDataSelector: (
    store: RootState
  ) =>
    | ReturnType<typeof selectACFormData>
    | ReturnType<typeof selectOCVehicleFormData>;
};

export const SendingProgress = ({
  formDataSelector,
}: SendingProgressProps): JSX.Element => {
  // TODO verify form depend on formType
  // useShouldRedirectToHomepage(selectIsFormValidated);
  const prevRoute = useSelector(selectPrevRoute);
  const formType = useSelector(selectFormType);
  const images = useSelector(selectImages);
  const formData = useSelector(formDataSelector);
  const hasError = useSelector(doesErrorOccured);
  const sentSucessfully = useSelector(isCaseSentSuccessfully);
  const dispatch = useDispatch();
  usePreventRefresh();

  useHideFooter();
  useEffect(() => {
    const handleSender = async () => {
      const uploadImagesActions = await prepareUploadImagesAction(images);

      for (const action of [
        () => postTextData({ data: formData, formType }),
        ...uploadImagesActions,
        () => generatePDF({ formType }),
        fulfillSender,
      ]) {
        const response = await dispatch(action());

        if (isRejected(response)) {
          break;
        }
      }
    };

    handleSender();
  }, [dispatch, formData, formType, images]);

  useEffect(() => {
    if (hasError) {
      dispatch(push(Url.SENDING_ERROR));
    }

    if (sentSucessfully) {
      dispatch(push(mapProgressRouteToSuccess[prevRoute]));
    }
  }, [prevRoute, dispatch, hasError, sentSucessfully]);

  return (
    <GenericScreen>
      <CardMedia component="img" src={icon} className={styles.icon} />
      <Heading
        align="center"
        color="secondary"
        component="h1"
        variant="h2"
        spacingBottom={{ xs: 4, sm: 6 }}
      >
        Trwa wysyłanie formularza
      </Heading>
      <Typography paragraph align="center">
        Proszę czekać - trwa wysyłanie zgłoszenia.
      </Typography>
    </GenericScreen>
  );
};
